import { set } from '@ember/object';
import { getOwner } from '@ember/owner';
import { renderComponent } from '@ember/renderer';
import { type Registry as Services, service } from '@ember/service';
import type { HasMany } from '@ember-data/model';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { PRISM_SPACING_100 } from '@mylighthouse/prism-foundation';
import { task, timeout } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import {
  COLOR_PARITY_LOSS,
  COLOR_PARITY_MEET,
  COLOR_PARITY_WIN,
} from 'frontend-utils/utils/css-variable';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import type { ParityInsightSegmentationItem } from 'lumen/models';
import { TOOLTIP_HOVER_TIMEOUT } from 'lumen/services/usage-analytics';
import type { ChartCustomPoint } from 'lumen/utils/constants/highcharts-constants';
import {
  PARITY_FILTER,
  type ParityFilter,
} from 'lumen/utils/constants/pi-constant-values';

import styles from './chart-variance.module.css';

interface VarianceBucket {
  number: number;
  variance: ParityInsightSegmentationItem | undefined;
}

interface VariancePoint {
  variance: ParityInsightSegmentationItem | undefined;
  x: number;
  y: number;
}

type VarianceSeries = Omit<Highcharts.SeriesColumnOptions, 'data'> & {
  data: VariancePoint[];
  metricId: ParityFilter;
};

interface VarianceChartSeries extends Highcharts.Series {
  options: Highcharts.SeriesOptionsType & { metricId: ParityFilter };
}

interface VarianceChartPoint extends ChartCustomPoint {
  series: VarianceChartSeries;
  variance?: ParityInsightSegmentationItem;
}

interface VarianceTooltipContext
  extends Highcharts.TooltipFormatterContextObject {
  point: VarianceChartPoint;
}

interface ParityChartsChartVarianceSignature {
  Args: {
    allVariances: HasMany<ParityInsightSegmentationItem> | undefined;
    hideHotelsMetric: boolean;
    parsedSegmentationFilters?: Record<string, string[]>;
    segmentationFilterClicked?: (
      segmentationId: string,
      segmentationValueId: string,
    ) => void;
    showLoadingPlaceholderState: boolean;
    totalNbOfShops?: number;
  };
  Element: HTMLElement;
}

export default class ParityChartsChartVariance extends Component<ParityChartsChartVarianceSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  declare chartOptions: Highcharts.Options;

  // Array to hold all possible buckets. This makes sure all graph indices are drawn even without data from API
  displayedVarianceBuckets = [
    -8, -7, -6, -5, -4, -3, -2, 1, 2, 3, 4, 5, 6, 7, 8,
  ];

  displayName = '';
  tagName = '';

  varianceMapping = {
    0: 'None',
    1: 'Meet',
    2: { valueA: 0, valueB: 5 },
    3: { valueA: 5, valueB: 10 },
    4: { valueA: 10, valueB: 15 },
    5: { valueA: 15, valueB: 20 },
    6: { valueA: 20, valueB: 25 },
    7: 25,
    8: 'Soldout',
  };

  trackHoverTask = task(
    { restartable: true },
    async (varianceBucket: string) => {
      await timeout(TOOLTIP_HOVER_TIMEOUT);
      this.usageAnalytics.track('Price Difference Details Viewed', {
        eventTrigger: 'price-difference-chart',
        varianceBucket,
      });
    },
  );

  get chartData(): VarianceSeries[] {
    const win: VariancePoint[] = [];
    const meet: VariancePoint[] = [];
    const loss: VariancePoint[] = [];

    if (this.args.allVariances) {
      this.displayedVarianceBuckets
        // Make buckets out of numbers array...
        .map((number) => {
          return {
            number,
            // @ts-expect-error: Object is possibly 'undefined'.
            variance: this.args.allVariances.filter(
              (variance) =>
                parseInt(variance.segmentationValueId, 10) === number,
            )[0],
          };
        })
        // Generate points win/meet/loss for each of the generated buckets
        .forEach((bucket) => {
          if (bucket.number === 1) {
            // Plot meet on 0
            meet.push(this.generatePoint(bucket, 'meet'));
          } else if (bucket.number > 0) {
            // Positive variance
            win.push(this.generatePoint(bucket, 'win'));
          } else if (bucket.number < 0) {
            // Negative variance
            loss.push(this.generatePoint(bucket, 'loss'));
          }
        });

      win.sort((a, b) => a.x - b.x);
      meet.sort((a, b) => a.x - b.x);
      loss.sort((a, b) => a.x - b.x);

      return [
        {
          type: 'column',
          metricId: PARITY_FILTER.LOSS,
          name: this.intl.t('parity_analytics.loss').toString(),
          data: loss,
          color: `var(${COLOR_PARITY_LOSS})`,
          visible: true,
          borderColor: 'var(--prism-color-border-inverse-emphasis)',
        },
        {
          type: 'column',
          metricId: PARITY_FILTER.MEET,
          name: this.intl.t('parity_analytics.meet').toString(),
          data: meet,
          color: `var(${COLOR_PARITY_MEET})`,
          visible: false,
          borderColor: 'var(--prism-color-border-inverse-emphasis)',
        },
        {
          type: 'column',
          metricId: PARITY_FILTER.WIN,
          name: this.intl.t('parity_analytics.win').toString(),
          data: win,
          color: `var(${COLOR_PARITY_WIN})`,
          visible: true,
          borderColor: 'var(--prism-color-border-inverse-emphasis)',
        },
      ];
    }

    return [];
  }

  constructor() {
    // @ts-expect-error: A spread argument must either have a tuple type or be passed to a rest parameter.
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    set(this, 'chartOptions', {
      chart: {
        zoomType: null,
        type: 'column',
        title: '',
        height: null, // Grow chart to fill container
        spacingLeft: 0,
        spacingRight: 0,
        spacingBottom: 0,
      },

      title: {
        text: 'parity-variance-chart',
      },

      tooltip: {
        shared: false,
        hideDelay: 0.2,
        shape: 'square',
        padding: 0,
        borderWidth: 0,
        shadow: false,
        outside: true,
        distance: 20,

        formatter(this: VarianceTooltipContext): string | null {
          const { point } = this;
          if (!point.variance) {
            return null;
          }

          const { metricId } = point.series.options;
          const element = document.createElement('div');
          renderComponent(ParityTooltipsTooltipKpi, {
            into: element,
            owner: getOwner(that),
            args: {
              data: {
                singleValue: point.variance.calculatedValues[metricId],
                singleValueTitle: that.intl.t(
                  'parity_analytics.percentage_total',
                ),
                totalNbOfHotels: point.variance.value.subscriptions,
                totalNbOfShops: point.variance.value[metricId],
              },
              displayName: that.xValue(point.x),
              hideShoppedHotels: that.args.hideHotelsMetric,
            },
          });

          return element.innerHTML;
        },
        useHTML: true,
      },

      xAxis: {
        tickInterval: 1,
        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            // @ts-expect-error: Argument of type 'string | number' is not assignable to parameter of type 'number'.
            return that.xValue(this.value);
          },
        },
      },

      yAxis: {
        // max: 100,
        title: {
          text: null,
        },
        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            // @ts-expect-error: Argument of type 'string | number' is not assignable to parameter of type 'number | bigint | StringNumericLiteral | null | undefined'.
            return that.intl.formatNumber(this.value, { style: 'percent' });
          },
        },
      },

      legend: {
        padding: PRISM_SPACING_100,
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        symbolWidth: 7,
        symbolHeight: 7,
        itemStyle: {
          fontSize: '12px',
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },

      plotOptions: {
        series: {
          pointPadding: 0.03,
          groupPadding: 0.03,
          shadow: false,
          pointWidth: null,
          stacking: 'normal',
        },

        column: {
          point: {
            events: {
              mouseOver(this: Highcharts.Point): void {
                void that.trackHoverTask.perform(that.xValue(this.x));
              },
              mouseOut(): void {
                void that.trackHoverTask.cancelAll();
              },
            },
          },
        },
      },
    });
  }

  generatePoint(bucket: VarianceBucket, key: ParityFilter): VariancePoint {
    let xValue = Math.abs(bucket.number) - 1;
    let yValue = 0;

    if (bucket.variance) {
      yValue = bucket.variance.calculatedValues[key];
    }

    if (bucket.number < 0) {
      xValue = -xValue;
    }

    return {
      x: xValue,
      y: yValue,
      variance: bucket.variance,
    };
  }

  xValue(point: number): string {
    const bucket = Math.abs(point) + 1;
    const varianceTranslationKey = tKey('parity_analytics.variance.until');
    // let priceDifferenceTextPrepend = '';

    // If long format is requested explicitly, set translations to long and prepend with 'Price discrepancy: '
    // if (isLongFormat) {
    //   translationKey = tKey('parity_analytics.variance.until.long');
    //   priceDifferenceTextPrepend = `${get(this, 'intl').t(tKey('parity_analytics.variance.discrepancy`)}: ');
    // }

    // 8 is the bucket for Soldout
    if (bucket === 8) {
      return this.intl.t('parity_analytics.variance.soldout').toString();
    }

    // 7 is the bucket for anything above - atm - 25
    if (bucket === 7) {
      const value = this.varianceMapping[bucket];

      if (point > 0) {
        return `+ ${value}%`;
      }

      return `- ${value}%`;
    }

    if (point > 0) {
      return `${this.intl.t(varianceTranslationKey, {
        // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{ 0: string; 1: string; ... }'.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        a: `${this.varianceMapping[bucket].valueA}%`,
        // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{ 0: string; 1: string; ... }'.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        b: `${this.varianceMapping[bucket].valueB}%`,
      })}`;
    }
    if (point < 0) {
      return `${this.intl.t(varianceTranslationKey, {
        // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{ 0: string; 1: string; ... }'.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        a: `${-this.varianceMapping[bucket].valueB}%`,
        // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{ 0: string; 1: string; ... }'.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        b: `${-this.varianceMapping[bucket].valueA}%`,
      })}`;
    }

    return `${this.intl.t('parity_analytics.meet')}`;
  }

  <template>
    <PrismCard ...attributes>
      <:header as |Header|>
        <div class="flex items-center">
          {{#if @showLoadingPlaceholderState}}
            <p class="loading-pulse--col-3"></p>
          {{else}}
            <Header.title>
              {{t "parity_analytics.variance_bucket"}}
            </Header.title>
          {{/if}}
        </div>
      </:header>
      <:content>
        <div class={{styles.breakdown-content}}>
          <GeneralChartsOtaChart
            @chartOptions={{this.chartOptions}}
            @model={{this.chartData}}
            class="h-full"
          />
        </div>
      </:content>
    </PrismCard>
  </template>
}
